/*
 * VatomInc. Copyright (c) 2022, all rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in the root directory of this source tree.
 */
import Hooks from './Hooks'
import Timer from './Timer'

/** Type of state */
export const AppStateType = {
    /** App is on the start screen */
    START: 0,
    /** App is running */
    RUNNING: 1,
    /** App has encountered an error */
    ERROR: 2,
}

/** Default buttons to use */
const defaultButtons = {
    primary: {
        text: 'Return to Space',
        action: e => location.reload()
    },
}

/**
 * Contains the state of the app.
 */
export default class AppState {

    /** @private Current state of the app. */
    static current = AppStateType.START

    /** Identifier of the current space. */
    static serverID = null

    /** Identifier of the business who the space belongs to. */
    static businessID = null

    /** Identifier of the dimension to use for the backend. */
    static dimensionID = null

    /** Details about the current space */
    static spaceDetails = null

    /** Name of the current branch */
    static branchName = 'production'

    /** `true` if allowed to leave without showing the popup, `false` otherwise. */
    static isAllowLeave = false

    /** `true` if the user has reloaded the space, `false` otherwise. */
    static hasReloaded = false

    /** `true` if the user has entered the space, `false` otherwise. */
    static hasEnteredSpace = false

    /** `true` if the user has finished validation, `false` otherwise. */
    static finishedValidation = false

    /** Information about the event that we have been redirected from, or `null` if not applicable */
    static redirectFromEvent = null

    /** `true` if we are starting the app, `false` otherwise. */
    static get isStarting() {
        return this.current === AppStateType.START
    }

    /** `true` if we are running the app, `false` otherwise. */
    static get isRunning() {
        return this.current === AppStateType.RUNNING
    }

    /** `true` if we have encountered an error, `false` otherwise. */
    static get isError() {
        return this.current === AppStateType.ERROR
    }

    /**
     * Updates the current state of the app.
     * @param {AppStateType} state New state.
     * @param {Error=} error Optional error that can be passed in.
     * @param {boolean=} shouldSend Optional. `true` if the error should be sent to Sentry, `false` otherwise. Default is `true`.
     */
    static update(state, error = null, shouldSend = true) {
        if (this.current === state) {
            return
        }

        // Trigger update
        this.current = state
        Hooks.get('core.app.state.change').trigger({ state, error, shouldSend })
    }

    /**
     * Indicates that we have encountered an error.
     * @param {string} msg Message to display.
     * @param {string} title Title of the message.
     * @param {string} icon Icon to display.
     * @param {object=} buttons Optional. Buttons to display.
     * @param {object=} buttons.primary Optional. Primary button to display. Default has text "Try Again" and action of reloading the page.
     * @param {string=} buttons.primary.text Optional. Text to display on the primary button. Default is "Try Again".
     * @param {Function=} buttons.primary.action Optional. Action to take when the primary button is pressed. Default is to reload the page.
     * @param {object=} buttons.secondary Optional. Secondary button to display.
     * @param {string=} buttons.secondary.text Optional. Text to display on the secondary button.
     * @param {Function=} buttons.secondary.action Optional. Action to take when the secondary button is pressed.
     * @param {string=} errorText Optional. Error text to display when clicking "Show Error". When not given, no "Show Error" button is displayed.
     * @param {boolean=} shouldSend Optional. `true` if the error should be sent to Sentry, `false` otherwise. Default is `true`.
     */
    static showError(msg, title, icon, buttons = defaultButtons, errorText = '', shouldSend = true) {
        const error = new Error(msg)
        error.title = title
        error.extra = errorText
        error.icon = icon
        error.buttons = buttons
        this.update(AppStateType.ERROR, error, shouldSend)
    }

    /** Hides the preloader that shows before any code is run */
    static hidePreloader() {
        // Allow React to have enough time to update
        Timer.setTimeout(e => {
            const overlay = document.getElementById('loading-overlay')
            if (!overlay) {
                return
            }

            // Fade out
            overlay.style.transition = 'opacity 0.7s ease-in-out'
            overlay.style.opacity = 0

            // Remove element
            Timer.setTimeout(e => {
                overlay.remove()
            }, 700)
        }, 400)
    }

}
